import * as preact from "preact";

import * as deviceDetect from "deviceDetect";
import * as iOSBridge from "iOSBridge";
import * as modalController from "modalController";
import { simpleMarkupToJsx } from "simpleMarkupJsx";
import Button from "ui/Button";
import Dialog from "views/Dialog";

type GenericDialogButton = {
  displayName: string;
  name: string;
};

type GenericDialogOptions = {
  buttons?: GenericDialogButton[];
  callback?: (result: string) => void;
  cancel?: string;
  icon?: string;
  message?: preact.ComponentChildren;
  ok?: string;
  title?: string;
  warning?: boolean;
};

type GenericDialogProps = {
  body: preact.ComponentChildren;
  onButtonClick: (name: string) => void;
  options: GenericDialogOptions;
};

const GenericDialog = ({
  body,
  onButtonClick,
  options,
}: GenericDialogProps) => (
  <Dialog
    class="genericDialog"
    headingIcon={options.icon}
    heading={options.title}
    actions={options.buttons?.map((button) => (
      <Button
        key={button.name}
        kind={button.name === "cancel" ? "secondary" : "primary"}
        name={button.name}
        // Enter confirms, unless confirming destroys something
        autoFocus={button.name === (options.warning ? "cancel" : "ok")}
        onClick={() => onButtonClick(button.name)}
      >
        {button.displayName}
      </Button>
    ))}
  >
    <p>{body}</p>
  </Dialog>
);

export const alert = (options: GenericDialogOptions) => {
  show(
    {
      icon: "warning-sign",
      buttons: [{ name: "ok", displayName: options.ok || "OK" }],
      ...options,
    },
    "Alert"
  );
};

export const confirm = (options: GenericDialogOptions) => {
  const okButton = {
    name: "ok",
    displayName: options.ok || "OK",
  };

  const cancelButton = {
    name: "cancel",
    displayName: options.cancel || "Cancel",
  };

  const confirmButtons = deviceDetect.is("windows")
    ? [okButton, cancelButton]
    : [cancelButton, okButton];

  show({
    icon: options.warning ? "warning-sign" : "question-sign",
    buttons: confirmButtons,
    ...options,
  });
};

/** The buttons of a native alert in the iOS app */
const nativeAlertButtons = (options: GenericDialogOptions) =>
  (options.buttons ?? []).map((button) => ({
    name: button.name,
    title: button.displayName,
    style:
      button.name === "cancel"
        ? ("cancel" as const)
        : options.warning && button.name === "ok"
          ? ("destructive" as const)
          : ("default" as const),
  }));

const show = (options: GenericDialogOptions, defaultTitle?: string) => {
  // In the iOS app, a native alert, unless the message needs more than text
  const showAlert = iOSBridge.get()?.showAlert;
  const message = options.message ?? "";
  if (showAlert && typeof message === "string") {
    void showAlert({
      title: options.title,
      message: message.replace(/\*\*/g, ""),
      buttons: nativeAlertButtons(options),
    }).then((name) => options.callback?.(name));
    return;
  }
  options = { title: defaultTitle, ...options };

  const body: preact.ComponentChildren =
    typeof options.message === "string"
      ? simpleMarkupToJsx(options.message)
      : options.message ?? null;
  let callback = options.callback;

  const runCallback = (result: string) => {
    callback?.(result);
    callback = undefined;
  };

  const handleButtonClick = (name: string) => {
    modalController.cancel(null, true);
    runCallback(name);
  };

  modalController.show(
    <GenericDialog
      body={body}
      onButtonClick={handleButtonClick}
      options={options}
    />,
    {
      compact: true,
      cancelCallback: () => runCallback("cancel"),
    }
  );
};
